/**
 * Linear pixel scales for hand-rolled bar/axis charts.
 *
 * `linearScale` maps a data value to a pixel offset the way `d3.scaleLinear`
 * would, without pulling in d3: it is a closure over one domain/range pair,
 * built once per chart and called once per data point. `bandPositions` is
 * the categorical counterpart — equal-width slots across a fixed pixel
 * width, each inset by a padding fraction, the same shape as `d3.scaleBand`.
 * Neither function knows about SVG elements or Svelte; they hand back plain
 * numbers a component turns into `x`/`width` attributes.
 *
 * Per src/lib/calc/numbers.ts: pure, never throws, never returns a
 * non-finite number.
 */

/**
 * `value -> px`, linear from 0 to `domainMax` onto 0 to `rangePx`.
 *
 * `domainMax === 0` (an empty or all-zero dataset) collapses every value to
 * 0 rather than dividing by zero into NaN or Infinity — a flat axis with
 * nothing on it, not a broken one.
 *
 * Rounded to 3 decimals, same as ./arc.ts: a bar's `x` (from `bandPositions`)
 * and its `y`/`height` (from this scale) land in the same `<rect>`, so both
 * need the same precision or rounding one of them buys nothing.
 */
export function linearScale(domainMax: number, rangePx: number): (value: number) => number {
  return (value: number) => {
    if (domainMax === 0 || !Number.isFinite(value) || !Number.isFinite(domainMax)) return 0;
    const scaled = (value / domainMax) * rangePx;
    return Number.isFinite(scaled) ? r3(scaled) : 0;
  };
}

export interface Band {
  x: number;
  width: number;
}

/**
 * `count` equal-width bands laid across `widthPx`, each inset by `padding`
 * (a fraction of its own slot, clamped to 0..1) on both sides so bars don't
 * touch. `count <= 0` returns `[]`: there are no slots to place.
 */
export function bandPositions(count: number, widthPx: number, padding: number): Band[] {
  if (!Number.isFinite(count) || count <= 0) return [];
  const slotWidth = Number.isFinite(widthPx) ? widthPx : 0;
  const slot = slotWidth / count;
  const inset = slot * clamp01(padding);
  const width = Math.max(0, slot - inset * 2);

  const bands: Band[] = [];
  for (let i = 0; i < count; i++) {
    bands.push({ x: r3(i * slot + inset), width: r3(width) });
  }
  return bands;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

function r3(value: number): number {
  return Math.round(value * 1000) / 1000;
}
